"use client"
import type { Editor, Extensions } from "@tiptap/core"
import { Node, mergeAttributes } from "@tiptap/core"
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight"
import Image from "@tiptap/extension-image"
import Link from "@tiptap/extension-link"
import Placeholder from "@tiptap/extension-placeholder"
import { EditorContent, useEditor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import bash from "highlight.js/lib/languages/bash"
import javascript from "highlight.js/lib/languages/javascript"
import python from "highlight.js/lib/languages/python"
import ruby from "highlight.js/lib/languages/ruby"
import typescript from "highlight.js/lib/languages/typescript"
import { createLowlight } from "lowlight"
import {
  Bold,
  Code,
  ImageIcon,
  Italic,
  LinkIcon,
  List,
  ListOrdered,
  Quote,
  Smile,
  Video,
} from "lucide-react"
import { useEffect, useMemo, useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { cn } from "@/lib/utils"

interface RichTextEditorProps {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  className?: string
  minHeight?: string
}

const COMMON_EMOJIS = [
  "😀",
  "😃",
  "😄",
  "😁",
  "😆",
  "😅",
  "😂",
  "🤣",
  "😊",
  "😇",
  "🙂",
  "🙃",
  "😉",
  "😌",
  "😍",
  "🥰",
  "😘",
  "😗",
  "😙",
  "😚",
  "😋",
  "😛",
  "😝",
  "😜",
  "🤪",
  "🤨",
  "🧐",
  "🤓",
  "😎",
  "🤩",
  "🥳",
  "😏",
  "😒",
  "😞",
  "😔",
  "😟",
  "😕",
  "🙁",
  "☹️",
  "😣",
  "😖",
  "😫",
  "😩",
  "🥺",
  "😢",
  "😭",
  "😤",
  "😠",
  "😡",
  "🤬",
  "🤯",
  "😳",
  "🥵",
  "🥶",
  "😱",
  "😨",
  "😰",
  "😥",
  "😓",
  "🤗",
  "🤔",
  "🤭",
  "🤫",
  "🤥",
  "😶",
  "😐",
  "😑",
  "😬",
  "🙄",
  "😯",
  "😦",
  "😧",
  "😮",
  "😲",
  "🥱",
  "😴",
  "🤤",
  "😪",
  "😵",
  "🤐",
  "🥴",
  "🤢",
  "🤮",
  "🤧",
  "😷",
  "🤒",
  "🤕",
  "🤑",
  "🤠",
  "😈",
  "👍",
  "👎",
  "👌",
  "✌️",
  "🤞",
  "🤟",
  "🤘",
  "🤙",
  "👈",
  "👉",
  "👆",
  "🖕",
  "👇",
  "☝️",
  "👋",
  "🤚",
  "🖐️",
  "✋",
  "🖖",
  "👏",
  "🙌",
  "🤲",
  "🤝",
  "🙏",
  "✍️",
  "💪",
  "🦾",
  "🦿",
  "🦵",
  "🦶",
  "❤️",
  "🧡",
  "💛",
  "💚",
  "💙",
  "💜",
  "🖤",
  "🤍",
  "🤎",
  "💔",
  "❣️",
  "💕",
  "💞",
  "💓",
  "💗",
  "💖",
  "💘",
  "💝",
  "💟",
  "☮️",
  "✨",
  "🎉",
  "🎊",
  "🔥",
  "💯",
  "⭐",
  "🌟",
  "💫",
  "⚡",
  "💥",
]

// Register a small, useful set of languages for syntax highlighting
const lowlight = createLowlight()
lowlight.register({ javascript })
lowlight.register({ typescript })
lowlight.register({ ruby })
lowlight.register({ python })
lowlight.register({ bash })

const MEDIA_STYLE =
  "display: block; max-width: 100%; width: min(100%, 640px); max-height: 360px; height: auto; object-fit: contain; border: 1px solid #ccc; border-radius: 8px; margin: 0 auto;"

const LINK_EXTENSION = Link.configure({ openOnClick: false, autolink: true })

const VideoExtension = Node.create({
  name: "video",
  group: "block",
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      src: {
        default: null,
      },
      controls: {
        default: true,
        renderHTML: (attributes) => ({
          controls: attributes.controls ? "true" : undefined,
        }),
      },
      autoplay: {
        default: false,
        renderHTML: (attributes) => ({
          autoplay: attributes.autoplay ? "true" : undefined,
        }),
      },
      loop: {
        default: false,
        renderHTML: (attributes) => ({
          loop: attributes.loop ? "true" : undefined,
        }),
      },
      muted: {
        default: false,
        renderHTML: (attributes) => ({
          muted: attributes.muted ? "true" : undefined,
        }),
      },
    }
  },
  parseHTML() {
    return [
      {
        tag: "video",
        getAttrs: (node) => {
          const src = node.getAttribute("src")
          const controls = node.hasAttribute("controls")
          return { src, controls }
        },
      },
    ]
  },
  renderHTML({ HTMLAttributes }) {
    return ["video", mergeAttributes({ style: MEDIA_STYLE }, HTMLAttributes)]
  },
})

export function RichTextEditor({
  value,
  onChange,
  placeholder = "Escribe aquí...",
  className,
  minHeight = "200px",
}: RichTextEditorProps) {
  const [isUrlDialogOpen, setIsUrlDialogOpen] = useState(false)
  const [urlInput, setUrlInput] = useState("")
  const [urlTextInput, setUrlTextInput] = useState("")

  const extensions = useMemo<Extensions>(
    () => [
      StarterKit.configure({
        blockquote: {},
        // Disable default codeBlock, we'll use CodeBlockLowlight for syntax highlighting
        codeBlock: false,
        heading: { levels: [1, 2, 3] },
      }),
      CodeBlockLowlight.configure({ lowlight }),
      LINK_EXTENSION,
      Image.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          style: MEDIA_STYLE,
        },
      }),
      Placeholder.configure({ placeholder }),
      VideoExtension,
    ],
    [placeholder],
  )

  const editor = useEditor({
    extensions,
    content: value ?? "",
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-sm max-w-none p-4 focus:outline-none",
          // compact spacing
          "prose-p:my-0 prose-blockquote:my-0 prose-pre:my-0 prose-ul:my-0 prose-ol:my-0 prose-li:my-0",
          "prose-h1:my-1 prose-h2:my-1 prose-h3:my-1 prose-h4:my-1 prose-h5:my-1 prose-h6:my-1",
          // blockquote + code styles
          "prose-blockquote:border-l-2 prose-blockquote:pl-3 prose-blockquote:not-italic prose-blockquote:quotes-none",
          "prose-pre:bg-muted prose-pre:p-3 prose-pre:rounded-md prose-pre:font-mono prose-pre:text-sm",
          "prose-code:!text-foreground prose-pre:!text-foreground",
          "min-h-[200px] text-foreground",
        ),
        style: `min-height: ${minHeight};`,
        "data-placeholder": placeholder ?? "",
        spellcheck: "true",
      },
    },
    onUpdate({ editor }: { editor: Editor }) {
      onChange(editor.getHTML())
    },
    autofocus: false,
  })

  // Sync external value -> editor only if changed outside
  useEffect(() => {
    if (!editor) return
    const current = editor.getHTML()
    if (value !== current) {
      editor.commands.setContent(value ?? "", false)
    }
  }, [editor, value])

  if (!editor) return null

  const setHeading = (level: 1 | 2 | 3) =>
    editor.chain().focus().toggleHeading({ level }).run()

  // Link dialog is opened via the toolbar button; no separate toggle function needed

  const handleUrlSubmit = () => {
    const url = urlInput.trim()
    const text = urlTextInput.trim()

    if (url === "") {
      editor.chain().focus().unsetLink().run()
    } else {
      if (text && !editor.state.selection.empty) {
        // If there's selected text, just add the link
        editor.chain().focus().setLink({ href: url }).run()
      } else if (text) {
        // If there's text input but no selection, insert new text with link
        editor
          .chain()
          .focus()
          .insertContent(`<a href="${url}">${text}</a>`)
          .run()
      } else {
        // If no text, just add the URL as both text and link
        editor
          .chain()
          .focus()
          .insertContent(`<a href="${url}">${url}</a>`)
          .run()
      }
    }

    setIsUrlDialogOpen(false)
    setUrlInput("")
    setUrlTextInput("")
  }

  const insertImageFromFile = () => {
    const input = document.createElement("input")
    input.type = "file"
    input.accept = "image/*"
    input.onchange = () => {
      const file = input.files?.[0] ?? null
      if (!file) return
      const form = new FormData()
      form.append("file", file)
      const meta = document.querySelector<HTMLMetaElement>(
        'meta[name="csrf-token"]',
      )
      const token = meta?.content ?? ""
      fetch("/uploads/images", {
        method: "POST",
        headers: {
          "X-CSRF-Token": token,
        },
        body: form,
        credentials: "same-origin",
      })
        .then(async (res) => {
          if (!res.ok) throw new Error(await res.text())
          return res.json() as Promise<{ url: string }>
        })
        .then(({ url }) => {
          if (!url) return
          editor.chain().focus().setImage({ src: url }).run()
        })
        .catch(() => {
          // noop for now
        })
    }
    input.click()
  }

  const insertVideoFromFile = () => {
    const input = document.createElement("input")
    input.type = "file"
    input.accept = "video/*"
    input.onchange = () => {
      const file = input.files?.[0] ?? null
      if (!file) return
      // Upload to server to get a persistent URL
      const form = new FormData()
      form.append("file", file)
      const meta = document.querySelector<HTMLMetaElement>(
        'meta[name="csrf-token"]',
      )
      const token = meta?.content ?? ""
      fetch("/uploads/videos", {
        method: "POST",
        headers: {
          "X-CSRF-Token": token,
        },
        body: form,
        credentials: "same-origin",
      })
        .then(async (res) => {
          if (!res.ok) throw new Error(await res.text())
          return res.json() as Promise<{ url: string }>
        })
        .then(({ url }) => {
          if (!url) return
          editor
            .chain()
            .focus()
            .insertContent({
              type: "video",
              attrs: { src: url, controls: true },
            })
            .run()
        })
        .catch(() => {
          // noop: could show a toast in the future
        })
    }
    input.click()
  }

  const insertEmoji = (emoji: string) => {
    editor.chain().focus().insertContent(emoji).run()
  }

  return (
    <div className={cn("w-full", className)}>
      <div className="bg-background rounded-lg border shadow-sm">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-1 border-b p-3">
          {/* Headings */}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={cn(
              "h-8 px-2",
              editor.isActive("heading", { level: 1 }) && "bg-muted",
            )}
            onClick={() => setHeading(1)}
            title="Título grande (H1)"
          >
            H1
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={cn(
              "h-8 px-2",
              editor.isActive("heading", { level: 2 }) && "bg-muted",
            )}
            onClick={() => setHeading(2)}
            title="Título (H2)"
          >
            H2
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={cn(
              "h-8 px-2",
              editor.isActive("heading", { level: 3 }) && "bg-muted",
            )}
            onClick={() => setHeading(3)}
            title="Subtítulo (H3)"
          >
            H3
          </Button>

          <Separator orientation="vertical" className="mx-2 h-4" />
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={cn("h-8 w-8 p-0", editor.isActive("bold") && "bg-muted")}
            onClick={() => editor.chain().focus().toggleBold().run()}
            title="Negrita"
          >
            <Bold className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={cn(
              "h-8 w-8 p-0",
              editor.isActive("italic") && "bg-muted",
            )}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            title="Cursiva"
          >
            <Italic className="h-4 w-4" />
          </Button>

          <Separator orientation="vertical" className="mx-2 h-4" />

          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={cn(
              "h-8 w-8 p-0",
              editor.isActive("bulletList") && "bg-muted",
            )}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            title="Lista con viñetas"
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={cn(
              "h-8 w-8 p-0",
              editor.isActive("orderedList") && "bg-muted",
            )}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            title="Lista numerada"
          >
            <ListOrdered className="h-4 w-4" />
          </Button>

          <Separator orientation="vertical" className="mx-2 h-4" />

          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={cn(
              "h-8 w-8 p-0",
              editor.isActive("blockquote") && "bg-muted",
            )}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            title="Cita"
          >
            <Quote className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={cn(
              "h-8 w-8 p-0",
              editor.isActive("codeBlock") && "bg-muted",
            )}
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            title="Bloque de código"
          >
            <Code className="h-4 w-4" />
          </Button>
          <Dialog open={isUrlDialogOpen} onOpenChange={setIsUrlDialogOpen}>
            <DialogTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0"
                title="Enlace"
              >
                <LinkIcon className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Insertar enlace</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="url">URL del enlace</Label>
                  <Input
                    id="url"
                    placeholder="https://ejemplo.com"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault()
                        handleUrlSubmit()
                      }
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="text">Texto del enlace (opcional)</Label>
                  <Input
                    id="text"
                    placeholder="Texto que se mostrará"
                    value={urlTextInput}
                    onChange={(e) => setUrlTextInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault()
                        handleUrlSubmit()
                      }
                    }}
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsUrlDialogOpen(false)
                    setUrlInput("")
                    setUrlTextInput("")
                  }}
                >
                  Cancelar
                </Button>
                <Button onClick={handleUrlSubmit}>
                  {urlInput.trim() === "" ? "Quitar enlace" : "Insertar enlace"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0"
            onClick={insertImageFromFile}
            title="Insertar imagen"
          >
            <ImageIcon className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0"
            onClick={insertVideoFromFile}
            title="Insertar video"
          >
            <Video className="h-4 w-4" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0"
                title="Insertar emoticón"
              >
                <Smile className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-80 p-2">
              <div className="grid max-h-48 grid-cols-10 gap-1 overflow-y-auto">
                {COMMON_EMOJIS.map((emoji, index) => (
                  <Button
                    key={index}
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="hover:bg-muted h-8 w-8 p-0 text-lg"
                    onClick={() => insertEmoji(emoji)}
                  >
                    {emoji}
                  </Button>
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Editor */}
        <EditorContent editor={editor} />
      </div>
    </div>
  )
}
